import { defineStore } from 'pinia';
import { ref, computed } from 'vue';
import { serviceManager, type ServiceStatus } from '@/services/service-manager/service-manager.service';
import { installerService } from '@/services/installer/installer.service';
import { updateService, type UpdateCheckResult } from '@/services/update/update.service';

export const useServiceStore = defineStore('service', () => {
  const status = ref<ServiceStatus>({ running: false });
  const isChecking = ref(false);
  const error = ref<string | null>(null);
  const isInstalling = ref(false);
  const updateInfo = ref<UpdateCheckResult | null>(null);
  const isCheckingUpdate = ref(false);

  const isServiceRunning = computed(() => status.value.running);
  const needsInstallation = computed(() => !status.value.running);

  async function checkStatus() {
    isChecking.value = true;
    error.value = null;
    try {
      const newStatus = await serviceManager.checkServiceStatus();
      status.value = newStatus;

      // Clear error if service is running
      if (newStatus.running) {
        error.value = null;
      }
    } catch (err: any) {
      // Don't log errors for normal cases (service not running is expected)
      // Only set error if it's a real problem
      const errorMessage = err.message || String(err);
      if (!errorMessage.includes('Failed to fetch') &&
        !errorMessage.includes('Load failed') &&
        !errorMessage.includes('network') &&
        !errorMessage.includes('timeout')) {
        // Unexpected error - set it
        error.value = errorMessage;
      }
      status.value = { running: false };
    } finally {
      isChecking.value = false;
    }
  }

  async function startService() {
    try {
      await serviceManager.startService();
      await checkStatus();
    } catch (err: any) {
      error.value = err.message;
      throw err;
    }
  }

  async function stopService() {
    try {
      await serviceManager.stopService();
      await checkStatus();
    } catch (err: any) {
      error.value = err.message;
      throw err;
    }
  }

  async function restartService() {
    try {
      await serviceManager.restartService();
      // Wait a bit for service to restart
      await new Promise(resolve => setTimeout(resolve, 2000));
      await checkStatus();
    } catch (err: any) {
      error.value = err.message;
      throw err;
    }
  }

  async function downloadInstaller() {
    isInstalling.value = true;
    error.value = null;
    try {
      await installerService.triggerDownload();
    } catch (err: any) {
      error.value = err.message;
      throw err;
    } finally {
      isInstalling.value = false;
    }
  }

  function getInstallInstructions() {
    const platform = installerService.detectPlatform();
    return installerService.getInstallInstructions(platform);
  }

  function getPlatformName() {
    const platform = installerService.detectPlatform();
    return installerService.getPlatformName(platform);
  }

  // Start periodic health check
  function startHealthCheck() {
    serviceManager.startHealthCheck((newStatus) => {
      status.value = newStatus;
    });
  }

  function stopHealthCheck() {
    serviceManager.stopHealthCheck();
  }

  async function checkForUpdate() {
    isCheckingUpdate.value = true;
    error.value = null;
    try {
      const result = await updateService.checkForUpdate(status.value.version);
      updateInfo.value = result;
      return result;
    } catch (err: any) {
      error.value = err.message;
      updateInfo.value = {
        updateAvailable: false,
        currentVersion: status.value.version || 'unknown',
        latestVersion: status.value.version || 'unknown'
      };
      throw err;
    } finally {
      isCheckingUpdate.value = false;
    }
  }

  const hasUpdate = computed(() => updateInfo.value?.updateAvailable ?? false);

  return {
    status,
    isChecking,
    error,
    isInstalling,
    isServiceRunning,
    needsInstallation,
    updateInfo,
    isCheckingUpdate,
    hasUpdate,
    checkStatus,
    startService,
    stopService,
    restartService,
    downloadInstaller,
    getInstallInstructions,
    getPlatformName,
    startHealthCheck,
    stopHealthCheck,
    checkForUpdate
  };
});

